/**
 * StagesBox — sequential lifecycle checklist with per-stage date editor
 * and gating rules. Includes the inline StageDateEditor.
 * Extracted from CompanyRegistrationDetailPage.jsx during the iter-238 refactor.
 */
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Check, X, Pencil } from 'lucide-react';
import { LIFECYCLE_STAGES } from './helpers';

// iter-231e (Feb 2026) — Mirrors AssignedTaskDetailPage.StageDateEditor.
// Renders a stage's timestamp inline with a pencil edit that toggles into
// a `datetime-local` input. Save pushes via the parent's `onSave` helper
// using the `stage_edit_index` + `stage_edit_data` fields on the backend
// PUT /admin/company-registrations/{id} endpoint.
function StageDateEditor({ stageKey, stageIndex, currentAt, by, isAr, canEdit, onSave, t }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const openEdit = () => {
    try {
      const d = new Date(currentAt);
      const pad = (n) => String(n).padStart(2, '0');
      const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      setValue(local);
    } catch {
      setValue('');
    }
    setEditing(true);
  };

  const submit = async () => {
    if (!value) {
      toast.error(t('اختر تاريخاً/وقتاً صحيحاً', 'Pick a valid date/time'));
      return;
    }
    if (stageIndex === undefined || stageIndex === null) {
      toast.error(t('تعذر تحديد المرحلة', 'Could not locate the stage'));
      return;
    }
    setSaving(true);
    const iso = new Date(value).toISOString();
    await onSave({
      stage_edit_index: stageIndex,
      stage_edit_data:  { stage: stageKey, date: iso },
    });
    setSaving(false);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid={`reg-stage-date-editor-${stageKey}`}>
        <input
          type="datetime-local"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="px-2 py-1 bg-black/50 border border-amber-500/40 rounded-md text-white text-[11px] focus:outline-none focus:border-amber-400 [color-scheme:dark]"
          data-testid={`reg-stage-date-input-${stageKey}`}
          disabled={saving}
        />
        <button
          type="button"
          onClick={submit}
          disabled={saving}
          className="p-1 rounded-md bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-50"
          title={t('حفظ', 'Save')}
          data-testid={`reg-stage-date-save-${stageKey}`}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          disabled={saving}
          className="p-1 rounded-md bg-white/5 text-white/60 hover:bg-white/10"
          title={t('إلغاء', 'Cancel')}
          data-testid={`reg-stage-date-cancel-${stageKey}`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="text-[11px] text-emerald-400/70 mt-0.5 flex items-center gap-1.5 flex-wrap" data-testid={`reg-stage-date-view-${stageKey}`} dir="ltr">
      <span className="font-mono">
        {new Date(currentAt).toLocaleString(isAr ? 'ar-EG' : 'en-GB')}
      </span>
      {by ? <span className="text-emerald-400/50">• {by}</span> : null}
      {canEdit && (
        <button
          type="button"
          onClick={openEdit}
          className="p-0.5 rounded text-white/40 hover:text-amber-300 hover:bg-amber-500/10 transition-colors"
          title={t('تعديل التاريخ', 'Edit date')}
          data-testid={`reg-stage-date-edit-btn-${stageKey}`}
        >
          <Pencil className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

export function StagesBox({ reg, onSave, t, saving, isAr }) {
  const doneKeys = useMemo(() => {
    const set = new Set();
    for (const s of (reg.stages || [])) {
      const raw = (s.stage || '').trim();
      const hit = LIFECYCLE_STAGES.find((L) => L.key === raw || L.ar === raw || L.en === raw);
      if (hit) set.add(hit.key);
    }
    return set;
  }, [reg.stages]);

  const isFinalised = doneKeys.has('closed');

  // Stage-date map (for showing timestamps like the assigned-tasks form).
  // iter-231e — Also captures the index in reg.stages so the inline
  // StageDateEditor can push updates via `stage_edit_index`.
  const stageInfo = useMemo(() => {
    const m = {};
    (reg.stages || []).forEach((s, i) => {
      const raw = (s.stage || '').trim();
      const hit = LIFECYCLE_STAGES.find((L) => L.key === raw || L.ar === raw || L.en === raw);
      if (hit) m[hit.key] = { at: s.date, by: s.actor, note: s.note, index: i };
    });
    return m;
  }, [reg.stages]);

  // Per-stage gate — returns null if allowed, else a blocker string.
  // iter-237 (Feb 2026) — expanded checks per user's step-by-step spec:
  //   • fees_collection    : license receipt + gov selection + admin_fees
  //                          card done + payment routing picked.
  //   • completed          : no extra conditions (parent-order stays).
  //   • commission_collect : admin_fees card done in EITHER mode; if
  //                          commission mode the receipt must be uploaded.
  //   • commission_sent    : agent card fully completed (mode picked, and
  //                          registered/unregistered rows have their data).
  //   • closed             : sequential lock handles the rest.
  const gateFor = (key) => {
    if (key === 'fees_collection') {
      // 1) License price + receipt uploaded
      const lf = reg?.license_fees || {};
      const lic = Number(lf.amount || 0);
      if (lic <= 0) return t('يجب إدخال رسوم الرخصة أولاً', 'Enter license fees first');
      if (!lf.receipt_url) return t('يجب رفع إيصال سعر الرخصة أولاً', 'Upload the license fee receipt first');
      // 2) Government fees card — must have been explicitly saved
      const gf = reg?.gov_fees || {};
      if (!('has_other_fees' in gf)) return t('حدّد بطاقة "رسوم الجهات الحكومية" (نعم/لا) أولاً', 'Pick "Government Fees" card (Yes/No) first');
      // 3) Service Fee Collection card — must have a mode + amount
      const af = reg?.admin_fees || {};
      if (!af.mode) return t('اختر وضع بطاقة "تحصيل الربح" (الرسوم الخاصة بنا / كومشن)', 'Pick "Profit Collection" mode (Our fees / Commission)');
      if (!af.amount || Number(af.amount) <= 0) return t('أكمل مبلغ بطاقة "تحصيل الربح"', 'Enter amount in "Profit Collection" card');
      if (af.mode === 'commission' && !af.receipt_url) return t('ارفع إيصال الكومشن في بطاقة "تحصيل الربح"', 'Upload the commission receipt in "Profit Collection"');
      // 4) Payment routing — iter-264e moved from a single
      // `payments_paid_to` to per-fee `paid_to` tags. Each fee row (license
      // + every gov entry) MUST now carry its own 'to_gov' / 'to_us' tag.
      // iter-299g (Feb 2026) — Gate rewritten to look at the real fields
      // instead of the legacy registration-level `payments_paid_to` which
      // the current UI no longer writes to.
      const licensePaidTo = reg?.license_fees?.paid_to;
      if (!licensePaidTo) {
        return t('اختر "مسدد لنا" أو "مسدد لجهة حكومية" في بطاقة "رسوم الرخصة"',
                 'Pick "Paid to us" or "Paid to gov" in the "License Fees" card');
      }
      const govEntries = (reg?.gov_fees?.entries) || [];
      for (let i = 0; i < govEntries.length; i += 1) {
        if (!govEntries[i].paid_to) {
          return t(`الرسم ${i + 1} في بطاقة "رسوم الجهات الحكومية": اختر طريقة السداد`,
                   `Fee #${i + 1} in "Government Fees" card: pick payment routing`);
        }
      }
      // Compute total owed to us from per-fee tags.
      const licToUs = licensePaidTo === 'to_us' ? lic : 0;
      const govToUs = govEntries
        .filter((e) => e.paid_to === 'to_us')
        .reduce((s, e) => s + Number(e.amount || 0), 0);
      const afMode  = af.mode || 'admin_fees';
      const adminToUs = afMode === 'admin_fees' ? Number(af.amount || 0) : 0;
      const due  = licToUs + govToUs + adminToUs;
      // iter-299h (Feb 2026) — PaymentsBox stores the receipt in
      // `reg.payments_receipt` (singular object), not `reg.payments`
      // (which was never written anywhere). Previously the gate read
      // the wrong field and always saw paid=0 → always blocked.
      const paymentReceipt = reg?.payments_receipt;
      let paid = 0;
      if (paymentReceipt && paymentReceipt.ai_verified) {
        paid = Number(paymentReceipt.amount || 0);
      }
      if (due > 0 && paid + 0.005 < due) {
        return t(`بقي ${(due - paid).toFixed(2)} AED من العميل`,
                 `Missing ${(due - paid).toFixed(2)} AED from customer`);
      }
    }
    if (key === 'commission_collection') {
      // Service Fee Collection card must be completed (mode + amount saved).
      const af = reg?.admin_fees || {};
      if (!af.mode) return t('أكمل بطاقة "تحصيل الربح" أولاً', 'Complete "Profit Collection" card first');
      if (!af.amount || Number(af.amount) <= 0) return t('أكمل مبلغ بطاقة "تحصيل الربح"', 'Enter amount in "Profit Collection" card');
      if (af.mode === 'commission' && !af.receipt_url) return t('ارفع إيصال الكومشن في بطاقة "تحصيل الربح"', 'Upload the commission receipt in "Profit Collection"');
    }
    if (key === 'commission_sent') {
      // Agent card must be fully completed.
      const ag = reg?.agent || {};
      if (!ag.mode) return t('حدّد بطاقة "الوكيل" أولاً', 'Pick the "Agent" card first');
      if (ag.mode === 'registered') {
        if (!ag.id) return t('اختر الوكيل المسجل من القائمة', 'Pick the registered agent from the list');
        if (!ag.commission_percent || Number(ag.commission_percent) <= 0) return t('أدخل نسبة عمولة الوكيل', 'Enter the agent commission %');
      }
      if (ag.mode === 'unregistered') {
        if (!ag.name || !ag.phone) return t('أدخل بيانات الوكيل غير المسجل (اسم + رقم)', 'Fill unregistered agent details (name + phone)');
        if (!ag.commission_percent || Number(ag.commission_percent) <= 0) return t('أدخل نسبة عمولة الوكيل', 'Enter the agent commission %');
      }
    }
    // iter-237.2 (Feb 2026) — The FINAL "Completed & Closed" stage cannot
    // be ticked until the operator has explicitly decided whether to open
    // a company file (`open_company_file === true | false`, not null).
    // If they picked "Yes", the license MUST also be saved (i.e. a
    // linked_company_file_id must exist) so we know the file was created.
    if (key === 'closed') {
      if (reg?.open_company_file !== true && reg?.open_company_file !== false) {
        return t(
          'حدّد في بطاقة "فتح ملف الشركة": نعم أو لا',
          'Pick Yes or No in the "Open Company File" card',
        );
      }
      if (reg?.open_company_file === true && !reg?.linked_company_file_id) {
        return t(
          'ارفع الرخصة واحفظها لإنشاء ملف الشركة أولاً',
          'Upload the license and save it to create the company file first',
        );
      }
    }
    return null;
  };

  // Toggle stage on/off. Because backend appends stages by name (idempotent
  // check is by presence), unchecking is a no-op locally (we allow only
  // moving forward — like assigned-tasks).
  const toggle = (key, checked) => {
    if (!checked) {
      toast.info(t('لا يمكن التراجع عن المرحلة بعد إتمامها', 'Cannot revert a completed stage'));
      return;
    }
    const blocker = gateFor(key);
    if (blocker) {
      toast.error(blocker);
      return;
    }
    onSave({ stage_add: { stage: key } });
  };

  return (
    <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-white font-bold text-sm">{t('مراحل الطلب', 'Request stages')}</h2>
        <span className="text-xs text-white/40">
          {t('(تسلسلية — أكمل السابقة أولاً)', '(sequential — complete the previous first)')}
        </span>
      </div>
      <ol className="space-y-1">
        {LIFECYCLE_STAGES.map((s, idx) => {
          const done   = doneKeys.has(s.key);
          const info   = stageInfo[s.key] || {};
          const Icon   = s.Icon;
          const prevDone = idx === 0 || doneKeys.has(LIFECYCLE_STAGES[idx - 1].key);
          const canToggle = !isFinalised && prevDone && !done;
          const blocker  = canToggle ? gateFor(s.key) : null;
          return (
            <div key={s.key} data-testid={`reg-stage-container-${s.key}`}>
              <li className={`flex items-center gap-3 p-3 rounded-lg border transition ${
                    done ? 'bg-emerald-500/5 border-emerald-500/30'
                         : canToggle ? 'bg-white/[0.02] border-white/10'
                                     : 'bg-white/[0.01] border-white/5 opacity-60'
                  }`}
                  data-testid={`reg-stage-row-${s.key}`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                      done ? 'bg-emerald-500/20 text-emerald-300'
                           : canToggle ? 'bg-white/10 text-white/60'
                                       : 'bg-white/5 text-white/25'
                    }`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white flex items-center gap-2">
                    <span className="text-white/40 text-xs font-mono">{idx + 1}.</span>
                    {isAr ? s.ar : s.en}
                  </div>
                  {done && info.at && (
                    <StageDateEditor
                      stageKey={s.key}
                      stageIndex={info.index}
                      currentAt={info.at}
                      by={info.by}
                      isAr={isAr}
                      canEdit={!isFinalised}
                      onSave={onSave}
                      t={t}
                    />
                  )}
                  {!done && !prevDone && (
                    <div className="text-[11px] text-white/30 mt-0.5">
                      {t('أكمل المرحلة السابقة أولاً', 'Complete the previous stage first')}
                    </div>
                  )}
                  {!done && prevDone && blocker && (
                    <div className="text-[11px] text-amber-300/80 mt-0.5">⚠ {blocker}</div>
                  )}
                </div>
                {/* iter-237 (Feb 2026) — removed the standalone
                    "تم التحصيل" / "تم الإرسال" checkboxes per user request.
                    All required conditions are now enforced via `gateFor()`
                    directly on the main stage-done checkmark, so the only
                    remaining control is the single stage-complete toggle. */}

                <label className={`inline-flex items-center gap-2 ${canToggle && !blocker ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                       data-testid={`reg-stage-toggle-${s.key}`}>
                  <input
                    type="checkbox"
                    checked={done}
                    disabled={!canToggle || !!blocker || saving}
                    onChange={(e) => toggle(s.key, e.target.checked)}
                    className="w-5 h-5 accent-[#d4af37] disabled:cursor-not-allowed"
                  />
                  <span className="text-xs text-white/60">
                    {done ? t('تم', 'Done') : t('غير مكتمل', 'Pending')}
                  </span>
                </label>
              </li>
            </div>
          );
        })}
      </ol>
    </div>
  );
}
